import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useLocation, useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    CheckboxFormInput,
    SelectFormInput,
    TextFormInput,
} from "src/components/form";
import {
    GroupTrialFinanceBreadcrumbs,
    TrialGroupFinanceBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "src/helpers/lookupHelper";
import useNavigate from "src/hooks/useNavigate";
import { RegistrationFormComponent } from "src/interfaces/forms/IRegistrationComponent";
import { ITrialGroupFinanceReduxFormState } from "src/reducers/reactReduxForms/trialGroupFinance";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialGroupActions from "../../../actions/trialGroup";
import * as TrialGroupFinanceActions from "../../../actions/trialGroupFinance";
import { NotificationAlert } from "../../../components/common/index";
import { RestrictedLayout } from "../../../components/layouts/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import {
    GroupTrialSubMenu,
    TrialGroupSubMenu,
} from "../../../helpers/subMenuHelper";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";

import "./TrialGroupFinancePage.scss";

interface TrialGroupFinancePageProps {
    form: Dtos.TrialGroupFinance & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: ITrialGroupFinanceReduxFormState;
    trialGroupFinance: Dtos.TrialGroupFinance;
    lookups: Dtos.Lookup[];
    loadingTrialGroupFinance: boolean;
    loadTrialGroupFinanceSuccess: boolean;
    loadTrialGroupFinanceFailure: boolean;
    loadTrialGroupFinanceErrorCode: string;
    savingTrialGroupFinance: boolean;
    saveTrialGroupFinanceSuccess: boolean;
    saveTrialGroupFinanceFailure: boolean;
    validatingTrialGroupFinance: boolean;
    validationFailures: Dtos.ResponseError[];
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    trialGroup: Dtos.TrialGroup;
    loadingTrialGroup: boolean;
    loadTrialGroupSuccess: boolean;
    loadTrialGroupFailure: boolean;
    loadTrialGroupErrorCode: string;
    clearTrialGroupFinance: TrialGroupFinanceActions.ITrialGroupFinanceClearActionCreator;
    loadTrialGroupFinance: TrialGroupFinanceActions.ITrialGroupFinanceLoadByTrialGroupIdActionCreator;
    saveTrialGroupFinance: TrialGroupFinanceActions.ITrialGroupFinanceSaveActionCreator;
    validateTrialGroupFinance: TrialGroupFinanceActions.ITrialGroupFinanceValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    clearTrialGroup: TrialGroupActions.ITrialGroupClearActionCreator;
    loadTrialGroup: TrialGroupActions.ITrialGroupLoadByIdActionCreator;
}

const reduxFormName: string = "reduxForms.trialGroupFinance";

const TrialGroupFinancePage: FC<TrialGroupFinancePageProps> = ({
    form,
    formState,
    formProperties,
    trialGroupFinance,
    lookups,
    loadingTrialGroupFinance,
    loadTrialGroupFinanceFailure,
    loadTrialGroupFinanceErrorCode,
    savingTrialGroupFinance,
    saveTrialGroupFinanceSuccess,
    saveTrialGroupFinanceFailure,
    validatingTrialGroupFinance,
    validationFailures,
    permissions,
    trialGroup,
    loadingTrialGroup,
    //loadTrialGroupSuccess,
    loadTrialGroupFailure,
    loadTrialGroupErrorCode,
    clearTrialGroupFinance,
    loadTrialGroupFinance,
    saveTrialGroupFinance,
    validateTrialGroupFinance,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems,
    clearTrialGroup,
    loadTrialGroup
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { groupId } = useParams<{ groupId: string }>();
    const { trialGroupId } = useParams<{ trialGroupId: string }>();
    const trialIdNumber = parseInt(trialId, 10);
    const groupIdNumber = parseInt(groupId, 10);
    const trialGroupIdNumber = parseInt(trialGroupId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();
    const location = useLocation();


    // Load Trial Group Finance
    useEffect(() => {
        if (trialGroupIdNumber && !trialGroupFinance) {
            const baseRoute = location?.pathname?.split("/")[1];

            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupFinanceBreadcrumbs
                    : GroupTrialFinanceBreadcrumbs;

            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber
                )
            );

            loadTrialGroupFinance(trialGroupIdNumber);
        } else if (trialGroupFinance && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }

        if (trialGroupIdNumber && !trialGroup && !loadingTrialGroup) {
            loadTrialGroup(trialGroupIdNumber);
        }
    }, [
        trialGroupIdNumber,
        trialGroupFinance,
        permissions,
        formInitalised,
        setDrawerItems,
        setBreadcrumbs,
        loadTrialGroupFinance,
        TrialGroupFinanceBreadcrumbs,
        GroupTrialFinanceBreadcrumbs,
        setFormInitalised,
        trialGroup,
        loadingTrialGroup,
        loadTrialGroup,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialGroupFinanceFailure) {
            navigate(`/error/${loadTrialGroupFinanceErrorCode}`);
        }
        if (loadTrialGroupFailure) {
            navigate(`/error/${loadTrialGroupErrorCode}`);
        }
    }, [
        loadTrialGroupFinanceFailure,
        loadTrialGroupFailure,
        loadTrialGroupFinanceErrorCode,
        loadTrialGroupErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (trialGroupIdNumber && trialGroup?.trial && trialGroup?.group) {
            const baseRoute = location?.pathname?.split("/")[1];

            const breadcrumbSetter =
                baseRoute === "trials"
                    ? TrialGroupFinanceBreadcrumbs
                    : GroupTrialFinanceBreadcrumbs;

            const drawerItemSetter =
                baseRoute === "trials" ? TrialGroupSubMenu : GroupTrialSubMenu;

            setDrawerItems(
                drawerItemSetter(
                    trialGroup?.groupId,
                    trialGroup?.trialId,
                    trialGroupIdNumber,
                    permissions
                )
            );
            setBreadcrumbs(
                breadcrumbSetter(
                    trialIdNumber,
                    groupIdNumber,
                    trialGroupIdNumber,
                    trialGroup.trial?.displayName,
                    trialGroup.group?.name
                )
            );
        }
    }, [
        trialIdNumber,
        groupIdNumber,
        trialGroupIdNumber,
        trialGroup,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        location,
    ]);

    // Reload trial group finance after saving
    useEffect(() => {
        if (!savingTrialGroupFinance && saveTrialGroupFinanceSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Trial group finance saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            const baseRoute = location?.pathname?.split("/")[1];

            if (saveAndReturn) {
                const url =
                    baseRoute === "trials"
                        ? `/trials/${trialIdNumber}/groups`
                        : `/groups/${groupIdNumber}/trials`;
                navigate(url);
            } else if (!trialGroupIdNumber) {
                const url =
                    baseRoute === "trials"
                        ? `/trials/${trialIdNumber}/groups/${groupIdNumber}/trialgroup/${trialGroupIdNumber}/finance`
                        : `/groups/${groupIdNumber}/trials/${trialIdNumber}/trialgroup/${trialGroupIdNumber}/finance`;
                navigate(url);
                clearTrialGroupFinance();
            }
        }
    }, [
        savingTrialGroupFinance,
        saveTrialGroupFinanceSuccess,
        trialGroupIdNumber,
        navigate,
        saveAndReturn,
        clearTrialGroupFinance,
        location,
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingTrialGroupFinance && saveTrialGroupFinanceFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingTrialGroupFinance, saveTrialGroupFinanceSuccess]);

    // Cleanup effect to clear the trial group & finance on component unmount
    useEffect(() => {
        return () => {
            clearTrialGroup();
            clearTrialGroupFinance();
            resetForm(reduxFormName);
        };
    }, [clearTrialGroup, clearTrialGroupFinance, resetForm, reduxFormName]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.TrialGroupFinance = {} as Dtos.TrialGroupFinance;

            if (!data.trialGroupId) {
                changes.trialGroupId = trialGroupIdNumber;
            }

            await saveTrialGroupFinance(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.TrialGroupFinance) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        const baseRoute = location?.pathname?.split("/")[1];
        const url =
            baseRoute === "trials"
                ? `/trials/${trialIdNumber}/groups`
                : `/groups/${groupIdNumber}/trials`;
        navigate(url);
    };

    const loadReduxForm = () => {
        let extraData: Dtos.TrialGroupFinance = {} as Dtos.TrialGroupFinance;

        loadForm(
            reduxFormName,
            Object.assign({ ...trialGroupFinance }, extraData)
        );
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.TrialGroupFinance = {} as Dtos.TrialGroupFinance;

            if (form && hasBeenSubmitted && !validatingTrialGroupFinance) {
                await validateTrialGroupFinance(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                trialGroupIdNumber,
                null,
                permissions,
                Dtos.Permission.TrialGroupFinanceUpdate,
                Dtos.Permission.TrialGroupFinanceCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrialGroupFinance ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroupFinance",
                                        {
                                            overrideFundingEmail: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Override Group Email Address",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroupFinance",
                                        {
                                            fundingEmail: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled:
                                                    formDisabled ||
                                                    !form.overrideFundingEmail,
                                                inputProps: {
                                                    value: !form.overrideFundingEmail
                                                        ? trialGroupFinance
                                                                ?.trialGroup
                                                                ?.group
                                                                ?.groupFinance
                                                                ?.fundingEmailAddress
                                                        : form.fundingEmail,
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroupFinance",
                                        {
                                            accountNumber: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trialGroupFinance",
                                        {
                                            bsbSwift: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-4",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-7 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-6">
                                    {renderFormControls(
                                        form,
                                        "trialGroupFinance",
                                        {
                                            siteCostCentre: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-4",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroupFinance",
                                        {
                                            overridePaymentDetails: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Override Payment Details",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroupFinance",
                                        {
                                            abn: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled:
                                                    formDisabled ||
                                                    !form.overridePaymentDetails,
                                                inputProps: {
                                                    value: !form.overridePaymentDetails
                                                        ? trialGroupFinance
                                                                ?.trialGroup
                                                                ?.group?.abn
                                                        : form.abn,
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row py-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroupFinance",
                                        {
                                            taxInvoiceName: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled:
                                                    formDisabled ||
                                                    !form.overridePaymentDetails,
                                                inputProps: {
                                                    value: !form.overridePaymentDetails
                                                        ? trialGroupFinance
                                                                ?.trialGroup
                                                                ?.group
                                                                ?.groupFinance
                                                                ?.taxInvoiceName
                                                        : form.taxInvoiceName,
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroupFinance",
                                        {
                                            quickbooksTaxCodeId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled:
                                                    formDisabled ||
                                                    !form.overridePaymentDetails,
                                                inputProps: {
                                                    value: !form.overridePaymentDetails
                                                        ? trialGroupFinance
                                                                ?.trialGroup
                                                                ?.group
                                                                ?.groupFinance
                                                                ?.quickbooksTaxCodeId
                                                        : form.quickbooksTaxCodeId,
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "GroupFinance",
                                                                "quickbooksTaxCodeId",
                                                                lookups
                                                            ),
                                                            form.quickbooksTaxCodeId
                                                        ),
                                                },
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName:
                                                    "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <Form
                model={reduxFormName}
                onSubmit={(val, _event) => handleOnSubmit(val)}
                onSubmitFailed={(val) => handleOnSubmitFailed(val)}
            >
                {renderFormControls(
                    form,
                    "trialGroupFinance",
                    formComponents,
                    lookups,
                    validationFailures,
                    formProperties,
                    changeForm
                )}
                <div className="mt-4 mb-4 text-right">
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", false);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit
                    </button>
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", true);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit And Return
                    </button>
                    <button onClick={onCancel} className="btn btn-primary mr-2">
                        Cancel
                    </button>
                </div>
            </Form>
        );
    }, [
        AuthenticationHelper,
        permissions,
        savingTrialGroupFinance,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        form,
        changeForm,
        reduxFormName,
        onCancel,
        trialGroupFinance,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.TrialGroupLabView}
            loading={loadingTrialGroupFinance || savingTrialGroupFinance}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialGroupFinancePageProps> => {
    let trialGroupFinance: Dtos.TrialGroupFinance | undefined = !(
        state.trialGroupFinance.formData instanceof Array
    )
        ? state.trialGroupFinance.formData
        : undefined;
    let trialGroup: Dtos.TrialGroup | undefined = !(
        state.trialGroup.formData instanceof Array
    )
        ? state.trialGroup.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialGroupFinance: trialGroupFinance!,
        loadingTrialGroupFinance:
            state.trialGroupFinance.loadState &&
            state.trialGroupFinance.loadState.status == RequestState.Pending,
        loadTrialGroupFinanceSuccess:
            state.trialGroupFinance.loadState &&
            state.trialGroupFinance.loadState.status == RequestState.Success,
        loadTrialGroupFinanceFailure:
            state.trialGroupFinance.loadState &&
            state.trialGroupFinance.loadState.status == RequestState.Failure,
        loadTrialGroupFinanceErrorCode:
            state.trialGroupFinance.loadState.errorCode,
        savingTrialGroupFinance:
            state.trialGroupFinance.saveState &&
            state.trialGroupFinance.saveState.status == RequestState.Pending,
        saveTrialGroupFinanceSuccess:
            state.trialGroupFinance.saveState &&
            state.trialGroupFinance.saveState.status == RequestState.Success,
        saveTrialGroupFinanceFailure:
            state.trialGroupFinance.saveState &&
            state.trialGroupFinance.saveState.status == RequestState.Failure,
        validatingTrialGroupFinance:
            state.trialGroupFinance.validateState &&
            state.trialGroupFinance.validateState.status ==
                RequestState.Pending,
        user: state.user.data,
        permissions: permissions,
        lookups: state.trialGroupFinance.lookups,
        form: state.reduxForms.trialGroupFinance,
        formState: state.trialGroupFinance.formState,
        formProperties: state.trialGroupFinance.formProperties,
        reduxFormState: state.reduxForms.formState.trialGroupFinance,
        validationFailures: trialGroupFinance
            ? state.trialGroupFinance.validationFailures
            : undefined,
        trialGroup: trialGroup!,
        loadingTrialGroup:
            state.trialGroup.loadState &&
            state.trialGroup.loadState.status == RequestState.Pending,
        loadTrialGroupSuccess:
            state.trialGroup.loadState &&
            state.trialGroup.loadState.status == RequestState.Success,
        loadTrialGroupFailure:
            state.trialGroup.loadState &&
            state.trialGroup.loadState.status == RequestState.Failure,
        loadTrialGroupErrorCode: state.trialGroup.loadState.errorCode,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrialGroupFinancePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadTrialGroupFinance: bindActionCreators(
            TrialGroupFinanceActions.LoadTrialGroupFinanceByTrialGroupId,
            dispatch
        ),
        clearTrialGroupFinance: bindActionCreators(
            TrialGroupFinanceActions.Clear,
            dispatch
        ),
        saveTrialGroupFinance: bindActionCreators(
            TrialGroupFinanceActions.SaveTrialGroupFinance,
            dispatch
        ),
        validateTrialGroupFinance: bindActionCreators(
            TrialGroupFinanceActions.ValidateTrialGroupFinance,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        loadTrialGroup: bindActionCreators(
            TrialGroupActions.LoadTrialGroupById,
            dispatch
        ),
        clearTrialGroup: bindActionCreators(TrialGroupActions.Clear, dispatch)
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialGroupFinancePage);
